import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import {
  useRecordar,
  type CursoDeLaPersona, type EstadoDeInduccion, type PersonaEnInduccion,
} from './api';

/**
 * TablaDeInduccion — una fila por persona, con cada curso obligatorio.
 *
 * En el móvil la tabla se desplaza dentro de su tarjeta, como las de
 * Solicitudes: la página no.
 */

/** Cómo se dice cada estado. El color acompaña; lo que informa es el texto. */
export const ESTADO: Record<EstadoDeInduccion, { texto: string; icono: string; tono: 'ok' | 'warn' | 'danger' }> = {
  al_dia: { texto: 'Al día', icono: 'bi-check-circle-fill', tono: 'ok' },
  pendiente: { texto: 'En progreso', icono: 'bi-hourglass-split', tono: 'warn' },
  sin_iniciar: { texto: 'Sin iniciar', icono: 'bi-x-circle-fill', tono: 'danger' },
};

/** En qué punto está una persona en UN curso. El vanilla pintaba el mismo
 *  círculo vacío para «sin empezar» y para «sin inscribir», que son dos
 *  problemas distintos: el segundo no se arregla recordándoselo. */
export function momentoDelCurso(c: CursoDeLaPersona) {
  if (c.completed) return { icono: 'bi-check-circle-fill', tinta: 'text-ok-ink', texto: 'terminado' };
  if (!c.enrolled) return { icono: 'bi-dash-circle-dotted', tinta: 'text-danger-ink', texto: 'sin inscribir' };
  if (c.progress_pct > 0) {
    return { icono: 'bi-circle-half', tinta: 'text-warn-ink', texto: `en curso, ${Math.round(c.progress_pct)} %` };
  }
  return { icono: 'bi-circle', tinta: 'text-muted', texto: 'sin empezar' };
}

export const LEYENDA = [
  { icono: 'bi-check-circle-fill', tinta: 'text-ok-ink', texto: 'terminado' },
  { icono: 'bi-circle-half', tinta: 'text-warn-ink', texto: 'en curso' },
  { icono: 'bi-circle', tinta: 'text-muted', texto: 'sin empezar' },
  { icono: 'bi-dash-circle-dotted', tinta: 'text-danger-ink', texto: 'sin inscribir' },
] as const;

export function TablaDeInduccion({
  personas, roles, actualizando, recordados, onRecordado,
}: {
  personas: PersonaEnInduccion[];
  roles: Array<{ key: string; name: string }>;
  /** Llegan otros filtros: la tabla de antes se queda, atenuada. */
  actualizando: boolean;
  recordados: ReadonlySet<number>;
  onRecordado: (id: number) => void;
}) {
  const nombreDelRol = new Map(roles.map((r) => [r.key, r.name]));
  return (
    <Card className={cn('overflow-x-auto', actualizando && 'opacity-60')} aria-busy={actualizando}>
      <table aria-label="Inducción de cada persona" className="w-full min-w-[54rem] border-collapse text-3">
        <thead>
          <tr className="border-b border-line text-left text-2 uppercase tracking-wide text-muted">
            <th scope="col" className="px-4 py-2.5 font-semibold">Persona</th>
            <th scope="col" className="px-4 py-2.5 font-semibold">Área</th>
            <th scope="col" className="px-4 py-2.5 font-semibold">Ingreso</th>
            <th scope="col" className="px-4 py-2.5 font-semibold">Avance</th>
            <th scope="col" className="px-4 py-2.5 font-semibold">Estado</th>
            <th scope="col" className="px-4 py-2.5"><span className="sr-only">Recordatorio</span></th>
          </tr>
        </thead>
        <tbody>
          {personas.map((p) => (
            <Fila
              key={p.user_id}
              p={p}
              rol={nombreDelRol.get(p.role) ?? p.role}
              recordado={recordados.has(p.user_id)}
              onRecordado={onRecordado}
            />
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Fila({
  p, rol, recordado, onRecordado,
}: {
  p: PersonaEnInduccion;
  rol: string;
  recordado: boolean;
  onRecordado: (id: number) => void;
}) {
  const estado = ESTADO[p.status];
  const sinInscribir = p.courses.filter((c) => !c.enrolled).length;
  const pct = Math.round(p.avg_progress);
  return (
    <tr className="border-b border-line align-top last:border-0">
      <td className="px-4 py-3">
        <p className="font-semibold text-ink">{p.full_name}</p>
        <p className="text-2 text-muted">{p.email} · {rol}</p>
        <CursosDeLaPersona cursos={p.courses} />
        {sinInscribir > 0 && (
          <p className="mt-1 flex items-center gap-1 text-1 font-semibold text-danger-ink">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            {sinInscribir === 1 ? '1 curso sin inscribir' : `${sinInscribir} cursos sin inscribir`}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-ink-2">{p.area_name ?? '—'}</td>
      <td className="whitespace-nowrap px-4 py-3 text-2 text-ink-2">
        {/* `fmtDateOnly`, no `fmtDate`: una fecha sin hora leída como
            medianoche UTC sale, en Bogotá, el día anterior. */}
        {p.hire_date ? Utils.fmtDateOnly(p.hire_date) : '—'}
      </td>
      <td className="min-w-40 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <BarraDeProgreso pct={pct} etiqueta={`Avance de ${p.full_name}`} />
          </div>
          <span className="text-2 font-bold tabular-nums text-ink-2">{pct} %</span>
        </div>
        <p className="mt-1 text-1 text-muted">
          {p.completed} de {p.total_mandatory} terminados
        </p>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <Badge tone={estado.tono}>
          <i className={cn('bi', estado.icono)} aria-hidden="true" /> {estado.texto}
        </Badge>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        {p.status !== 'al_dia' && (
          <BotonRecordar persona={p} recordado={recordado} onRecordado={onRecordado} />
        )}
      </td>
    </tr>
  );
}

function CursosDeLaPersona({ cursos }: { cursos: CursoDeLaPersona[] }) {
  return (
    <ul className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1" aria-label="Cursos obligatorios">
      {cursos.map((c) => {
        const m = momentoDelCurso(c);
        return (
          <li key={c.course_id} className="flex min-w-0 items-center gap-1 text-1 text-ink-2">
            <i className={cn('bi', m.icono, m.tinta)} aria-hidden="true" />
            {/* El vanilla cortaba a 18 letras, sin puntos suspensivos. */}
            <span className="max-w-[14rem] truncate" title={`${c.title}: ${m.texto}`}>{c.title}</span>
            <span className="sr-only">: {m.texto}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Cada botón con SU mutación. Con una sola para toda la tabla, pulsar otro
 *  mientras salía el primero se tragaba el aviso del primero: TanStack solo
 *  llama al `onSuccess` de la última llamada a `mutate`. */
function BotonRecordar({
  persona, recordado, onRecordado,
}: {
  persona: PersonaEnInduccion;
  recordado: boolean;
  onRecordado: (id: number) => void;
}) {
  const recordar = useRecordar();
  if (recordado) {
    return (
      <span className="inline-flex items-center gap-1 text-2 font-semibold text-ok-ink">
        <i className="bi bi-envelope-check" aria-hidden="true" /> Enviado
      </span>
    );
  }
  return (
    <Button
      variant="secondary"
      size="sm"
      loading={recordar.isPending}
      aria-label={`Recordar la inducción a ${persona.full_name}`}
      onClick={() =>
        recordar.mutate(persona.user_id, {
          onSuccess: (r) => {
            Utils.toast(r.message, r.sent ? 'success' : 'info');
            if (r.sent) onRecordado(persona.user_id);
          },
          onError: (err) =>
            Utils.toast(err instanceof Error ? err.message : 'No se pudo enviar el recordatorio', 'error'),
        })
      }
    >
      <i className="bi bi-envelope" aria-hidden="true" /> Recordar
    </Button>
  );
}
